import React, {useState, useEffect, useMemo, useCallback} from 'react';
import {Modal, Form, Row, Col, Dropdown} from 'react-bootstrap';
import {Tooltip} from '@/components/tooltips';
import {Button, Badge} from '@/components/ui';
import {Icon} from '@/components/icons/Icon';
import axios from 'axios';
import Select from 'react-select';
import TableComponent from '@/components/table/Table';
import HelpTextBlock from '@/components/help_text/HelpTextBlock';
import SearchBox from '@/components/search/SearchBox';
import Spinner from '@/components/ui/Spinner';
import {ModalAlert} from '@/components/alerts';
import i18n from 'i18n';
import {buildURL} from '@/utils/urlBuilder';
import RestrictionProfileDetailModal from './RestrictionProfileDetailModal';

const PROFILES_URL = '/internal_api/v1/admin/restriction-profiles';

interface RestrictionProfile {
    id: number;
    name: string;
    codename: string;
    description: string | null;
    platform_managed: boolean;
    field_count: number;
    role_count: number;
    role_names: string[];
}

// --- Create/Edit Restriction Profile Modal ---
const CreateRestrictionProfileModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onSaved: () => void;
    editProfile?: RestrictionProfile | null;
}> = ({isOpen, onClose, onSaved, editProfile}) => {
    const [formData, setFormData] = useState({name: '', description: ''});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);

    const isEditing = !!editProfile;

    useEffect(() => {
        if (isOpen) {
            if (editProfile) {
                setFormData({name: editProfile.name, description: editProfile.description || ''});
            } else {
                setFormData({name: '', description: ''});
            }
            setErrors({});
        }
    }, [isOpen, editProfile]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const newErrors: Record<string, string> = {};
        if (!formData.name.trim()) newErrors.name = i18n.t('restriction_profiles_name_required.text', {defaultValue: 'Name is required'});
        if (Object.keys(newErrors).length > 0) {setErrors(newErrors); return;}

        setSaving(true);
        try {
            if (isEditing) {
                await axios.put(`${PROFILES_URL}/${editProfile!.id}`, formData);
            } else {
                await axios.post(PROFILES_URL, formData);
            }
            onSaved();
            onClose();
        } catch (err: any) {
            const fallback = isEditing
                ? i18n.t('restriction_profiles_update_failed.text', {defaultValue: 'Failed to update profile'})
                : i18n.t('restriction_profiles_create_failed.text', {defaultValue: 'Failed to create profile'});
            setErrors({name: err?.response?.data?.error || fallback});
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={isOpen} onHide={onClose}>
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {isEditing
                            ? i18n.t('restriction_profiles_edit_title.text', {defaultValue: 'Edit restriction profile'})
                            : i18n.t('restriction_profiles_create_title.text', {defaultValue: 'Create restriction profile'})}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            {i18n.t('jt_name.text', {defaultValue: 'Name'})} <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            value={formData.name}
                            onChange={e => setFormData(prev => ({...prev, name: e.target.value}))}
                            isInvalid={!!errors.name}
                            placeholder={i18n.t('restriction_profiles_name_placeholder.text', {defaultValue: 'e.g. Agent Performance Metrics'})}
                        />
                        <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{i18n.t('jt_description.text', {defaultValue: 'Description'})}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={formData.description}
                            onChange={e => setFormData(prev => ({...prev, description: e.target.value}))}
                            placeholder={i18n.t('restriction_profiles_description_placeholder.text', {defaultValue: 'Describe what this profile restricts'})}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" type="button" onClick={onClose} disabled={saving}>{i18n.t('jt_cancel.text', {defaultValue: 'Cancel'})}</Button>
                    <Button variant="primary" type="submit" disabled={saving}>
                        {saving
                            ? i18n.t('restriction_profiles_saving.text', {defaultValue: 'Saving...'})
                            : isEditing
                                ? i18n.t('restriction_profiles_update_btn.text', {defaultValue: 'Save changes'})
                                : i18n.t('restriction_profiles_create_btn.text', {defaultValue: 'Create profile'})}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

// --- Assign Roles Modal ---
const AssignRolesModal: React.FC<{
    profileId: number | null;
    profileName: string;
    onClose: () => void;
    onSaved: () => void;
}> = ({profileId, profileName, onClose, onSaved}) => {
    const [availableRoles, setAvailableRoles] = useState<{id: number; name: string}[]>([]);
    const [selectedRoleIds, setSelectedRoleIds] = useState<Set<number>>(new Set());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    useEffect(() => {
        if (!profileId) return;
        setLoading(true);
        Promise.all([
            axios.get(`${PROFILES_URL}/${profileId}/roles`),
            axios.get(buildURL('adminRolesList')),
        ]).then(([profileRolesRes, rolesRes]) => {
            setAvailableRoles(rolesRes.data.roles || []);
            setSelectedRoleIds(new Set(profileRolesRes.data.role_ids || []));
        }).catch(() => {
            setAvailableRoles([]);
        }).finally(() => setLoading(false));
    }, [profileId]);

    const handleSave = async () => {
        if (!profileId) return;
        setSaving(true);
        setSaveError(null);
        try {
            await axios.put(`${PROFILES_URL}/${profileId}/roles`, {
                role_ids: Array.from(selectedRoleIds),
            });
            onSaved();
            onClose();
        } catch (err: any) {
            setSaveError(err?.response?.data?.error || i18n.t('restriction_profiles_assign_roles_failed.text', {defaultValue: 'Failed to save role assignments. Please try again.'}));
        } finally {
            setSaving(false);
        }
    };

    if (!profileId) return null;

    return (
        <Modal show={!!profileId} onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    {i18n.t('restriction_profiles_assign_roles_title.text', {defaultValue: 'Assign roles — {{name}}', name: profileName})}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="text-center p-4">
                        <Spinner text={i18n.t('sp_loading.text')} centered />
                    </div>
                ) : (
                    <>
                        <p className="text-muted small mb-3">
                            {i18n.t('restriction_profiles_assign_roles_description.text', {defaultValue: 'Select the roles that will be restricted by this profile. Users in selected roles will not see fields included in this profile.'})}
                        </p>
                        <Select
                            isMulti
                            options={availableRoles.map(r => ({value: r.id, label: r.name}))}
                            value={Array.from(selectedRoleIds).map(id => {
                                const role = availableRoles.find(r => r.id === id);
                                return role ? {value: role.id, label: role.name} : null;
                            }).filter(Boolean)}
                            onChange={(selected) => {
                                const ids = new Set<number>((selected || []).map((s: any) => s.value));
                                setSelectedRoleIds(ids);
                            }}
                            placeholder={i18n.t('restriction_profiles_select_roles_placeholder.text', {defaultValue: 'Select roles...'})}
                            isClearable
                            isSearchable
                            menuPlacement="bottom"
                            styles={{
                                control: (base) => ({...base, minHeight: '38px'}),
                                menu: (base) => ({...base, zIndex: 10}),
                            }}
                        />
                        <div className="mt-2 d-flex align-items-center">
                            <small className="text-muted">
                                {i18n.t('field_restrictions_n_selected.text', {defaultValue: '{{count}} selected', count: selectedRoleIds.size})}
                            </small>
                            {selectedRoleIds.size > 0 && (
                                <>
                                    <small className="text-muted mx-2">&middot;</small>
                                    <span
                                        className="text-primary small"
                                        style={{cursor: 'pointer'}}
                                        onClick={() => setSelectedRoleIds(new Set())}
                                    >
                                        {i18n.t('field_restrictions_clear_all.text', {defaultValue: 'Clear all'})}
                                    </span>
                                </>
                            )}
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                {saveError && (
                    <div className="text-danger small mr-auto">{saveError}</div>
                )}
                <Button variant="secondary" onClick={onClose}>
                    {i18n.t('jt_cancel.text', {defaultValue: 'Cancel'})}
                </Button>
                <Button variant="primary" onClick={handleSave} loading={saving}>
                    {i18n.t('field_restrictions_apply.text', {defaultValue: 'Apply'})}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// --- Main View ---
const RestrictionProfilesView: React.FC = () => {
    const [profiles, setProfiles] = useState<RestrictionProfile[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editProfile, setEditProfile] = useState<RestrictionProfile | null>(null);
    const [detailProfileId, setDetailProfileId] = useState<number | null>(null);
    const [assignRolesProfile, setAssignRolesProfile] = useState<RestrictionProfile | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [profileToDelete, setProfileToDelete] = useState<RestrictionProfile | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);

    const loadProfiles = useCallback(() => {
        setLoading(true);
        axios.get(PROFILES_URL)
            .then(res => {
                setProfiles(res.data?.profiles || []);
                setError(null);
            })
            .catch(err => {
                setError(err?.response?.data?.error || i18n.t('restriction_profiles_load_profiles_failed.text', {defaultValue: 'Failed to load profiles'}));
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {loadProfiles();}, [loadProfiles]);

    const openDeleteConfirm = useCallback((profile: RestrictionProfile) => {
        setProfileToDelete(profile);
        setDeleteConfirmOpen(true);
    }, []);

    const closeDeleteConfirm = useCallback(() => {
        setDeleteConfirmOpen(false);
        setProfileToDelete(null);
        setIsDeleting(false);
        setDeleteError(null);
    }, []);

    const confirmDelete = useCallback(async () => {
        if (!profileToDelete) return;
        setIsDeleting(true);
        setDeleteError(null);
        try {
            await axios.delete(`${PROFILES_URL}/${profileToDelete.id}`);
            closeDeleteConfirm();
            loadProfiles();
        } catch (err: any) {
            setDeleteError(err?.response?.data?.error || i18n.t('restriction_profiles_delete_failed.text', {defaultValue: 'Failed to delete profile. Please try again.'}));
            setIsDeleting(false);
        }
    }, [profileToDelete, closeDeleteConfirm, loadProfiles]);

    const filteredProfiles = useMemo(() => {
        let result = profiles;
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            result = result.filter(p =>
                p.name.toLowerCase().includes(q) ||
                p.codename.toLowerCase().includes(q) ||
                (p.description || '').toLowerCase().includes(q)
            );
        }
        return [...result].sort((a, b) => {
            if (a.platform_managed === b.platform_managed) return a.name.localeCompare(b.name);
            return a.platform_managed ? -1 : 1;
        });
    }, [profiles, searchQuery]);

    const columns = [
        {
            header: i18n.t('jt_name.text', {defaultValue: 'Name'}),
            accessorKey: 'name',
            cell: ({getValue, row}: {getValue: () => string; row: {original: RestrictionProfile}}) => {
                const name = getValue();
                const profile = row.original;
                return (
                    <div className="d-flex align-items-center">
                        <span className="fw-bold">{name}</span>
                        {profile.platform_managed && (
                            <Badge variant="secondary" className="ml-2">{i18n.t('roles_system_badge.text', {defaultValue: 'System'})}</Badge>
                        )}
                    </div>
                );
            },
        },
        {
            header: i18n.t('jt_description.text', {defaultValue: 'Description'}),
            accessorKey: 'description',
            cell: ({getValue}: {getValue: () => string}) => {
                const desc = getValue();
                if (!desc) return '-';
                if (desc.length > 120) {
                    return (
                        <Tooltip content={desc} placement="top">
                            <span>{desc.substring(0, 120)}...</span>
                        </Tooltip>
                    );
                }
                return desc;
            },
        },
        {
            header: i18n.t('restriction_profiles_col_fields.text', {defaultValue: 'Fields'}),
            accessorKey: 'field_count',
            cell: ({getValue}: {getValue: () => number}) => {
                const count = getValue();
                return (
                    <Badge variant="info">
                        {count} {count === 1 ? i18n.t('restriction_profiles_field_singular.text', {defaultValue: 'field'}) : i18n.t('restriction_profiles_field_plural.text', {defaultValue: 'fields'})}
                    </Badge>
                );
            },
        },
        {
            header: i18n.t('restriction_profiles_col_roles.text', {defaultValue: 'Roles'}),
            accessorKey: 'role_names',
            cell: ({row}: {row: {original: RestrictionProfile}}) => {
                const roleNames = row.original.role_names || [];
                if (roleNames.length === 0) {
                    return <span className="text-muted">—</span>;
                }
                return (
                    <div>
                        {roleNames.map((name) => (
                            <div key={name} className="mb-1">
                                <Badge variant="primary">
                                    {name}
                                </Badge>
                            </div>
                        ))}
                    </div>
                );
            },
        },
        {
            header: i18n.t('jt_actions.text', {defaultValue: 'Actions'}),
            cell: ({row}: {row: {original: RestrictionProfile}}) => {
                const profile = row.original;
                return (
                    <div className="d-inline-block">
                        <Tooltip content={i18n.t('jt_actions.text', {defaultValue: 'Actions'})} placement="top">
                            <Dropdown className="d-inline-block">
                                <Dropdown.Toggle
                                    as={Button}
                                    variant="outline-info"
                                    size="sm"
                                    icon="mdi-dots-horizontal"
                                    className="dropdown-toggle-no-caret"
                                    id={`profile-dropdown-${profile.id}`}
                                />
                                <Dropdown.Menu align="end" renderOnMount popperConfig={{strategy: 'fixed'}}>
                                    <Dropdown.Item onClick={() => setDetailProfileId(profile.id)}>
                                        <Icon name="mdi-eye" className="mr-2" />
                                        {i18n.t('restriction_profiles_view_details.text', {defaultValue: 'View details'})}
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => setAssignRolesProfile(profile)}>
                                        <Icon name="mdi-account-group" className="mr-2" />
                                        {i18n.t('restriction_profiles_assign_roles.text', {defaultValue: 'Assign roles'})}
                                    </Dropdown.Item>
                                    {profile.platform_managed ? (
                                        <Tooltip content={i18n.t('restriction_profiles_system_cannot_edit.text', {defaultValue: 'System profiles cannot be edited'})} placement="top">
                                            <div style={{cursor: 'not-allowed', display: 'flex'}}>
                                                <Dropdown.Item style={{pointerEvents: 'none'}} disabled>
                                                    <Icon name="mdi-pencil" className="mr-2" />
                                                    {i18n.t('restriction_profiles_edit.text', {defaultValue: 'Edit'})}
                                                </Dropdown.Item>
                                            </div>
                                        </Tooltip>
                                    ) : (
                                        <Dropdown.Item onClick={() => setEditProfile(profile)}>
                                            <Icon name="mdi-pencil" className="mr-2" />
                                            {i18n.t('restriction_profiles_edit.text', {defaultValue: 'Edit'})}
                                        </Dropdown.Item>
                                    )}
                                    {profile.platform_managed ? (
                                        <Tooltip content={i18n.t('restriction_profiles_system_cannot_delete.text', {defaultValue: 'System profiles cannot be deleted'})} placement="top">
                                            <div style={{cursor: 'not-allowed', display: 'flex'}}>
                                                <Dropdown.Item style={{pointerEvents: 'none'}} disabled>
                                                    <Icon name="mdi-delete" className="mr-2" />
                                                    {i18n.t('jt_delete.text', {defaultValue: 'Delete'})}
                                                </Dropdown.Item>
                                            </div>
                                        </Tooltip>
                                    ) : (
                                        <Dropdown.Item onClick={() => openDeleteConfirm(profile)}>
                                            <Icon name="mdi-delete" className="mr-2" />
                                            {i18n.t('jt_delete.text', {defaultValue: 'Delete'})}
                                        </Dropdown.Item>
                                    )}
                                </Dropdown.Menu>
                            </Dropdown>
                        </Tooltip>
                    </div>
                );
            },
        },
    ];

    if (loading) return <div className="p-4">{i18n.t('sp_loading.text', {defaultValue: 'Loading...'})}</div>;
    if (error) return <div className="p-4 text-danger">{i18n.t('restriction_profiles_error.text', {defaultValue: 'Error: {{error}}', error})}</div>;

    return (
        <>
            <HelpTextBlock helpText={i18n.t('restriction_profiles_help_text.text', {defaultValue: 'Restriction profiles are named collections of fields that can be assigned to roles. When a profile is assigned to a role, users in that role will not see the fields in that profile. Platform-managed profiles are maintained by the system and cannot be edited.'})} />

            <Row>
                <Col lg={3}>
                    <SearchBox
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder={i18n.t('restriction_profiles_search_placeholder.text', {defaultValue: 'Search profiles...'})}
                    />
                </Col>
                <Col lg={9} className="d-flex justify-content-end">
                    <Button
                        variant="primary"
                        size="sm"
                        className="mb-2"
                        icon="mdi-plus"
                        onClick={() => setCreateModalOpen(true)}
                    >
                        {i18n.t('restriction_profiles_add_btn.text', {defaultValue: 'Add profile'})}
                    </Button>
                </Col>
            </Row>

            <div className="card">
                <div className="card-body">
                    {filteredProfiles.length === 0 ? (
                        <div className="text-center text-muted py-4">
                            {searchQuery ? i18n.t('restriction_profiles_no_search_results.text', {defaultValue: 'No profiles match your search'}) : i18n.t('restriction_profiles_empty.text', {defaultValue: 'No restriction profiles configured yet'})}
                        </div>
                    ) : (
                        <TableComponent data={filteredProfiles} columns={columns} />
                    )}
                </div>
            </div>

            <CreateRestrictionProfileModal
                isOpen={createModalOpen || !!editProfile}
                onClose={() => {setCreateModalOpen(false); setEditProfile(null);}}
                onSaved={loadProfiles}
                editProfile={editProfile}
            />

            <RestrictionProfileDetailModal
                profileId={detailProfileId}
                onClose={() => setDetailProfileId(null)}
                onSaved={loadProfiles}
            />

            <AssignRolesModal
                profileId={assignRolesProfile?.id || null}
                profileName={assignRolesProfile?.name || ''}
                onClose={() => setAssignRolesProfile(null)}
                onSaved={loadProfiles}
            />

            <ModalAlert
                isOpen={deleteConfirmOpen}
                onClose={closeDeleteConfirm}
                variant="error"
                title={i18n.t('restriction_profiles_delete_title.text', {defaultValue: 'Delete profile'})}
                message={
                    (profileToDelete
                        ? i18n.t('restriction_profiles_delete_confirm.text', {defaultValue: 'Delete "{{name}}"? This removes all role assignments for this profile.', name: profileToDelete.name})
                        : '')
                    + (deleteError ? `\n\n${deleteError}` : '')
                }
                actions={[
                    {
                        label: i18n.t('restriction_profiles_delete_title.text', {defaultValue: 'Delete profile'}),
                        onClick: confirmDelete,
                        variant: 'danger',
                        loading: isDeleting,
                    }
                ]}
                dismissible={!isDeleting}
            />
        </>
    );
};

export default RestrictionProfilesView;
